import { URLS_PER_REQUEST_MAX } from "@typedoc/contracts/files";
import { AppError } from "../app-error";
import { contractRequest, type ContractCall } from "../contract-request";
import type { RegionalOptions } from "../regional-request";
import { filesApiClient, type FilesApiClient } from "../rpc/files";
import { mediaTypeOf } from "./check";
import { putWithProgress, type Put } from "./put";
import type { FilesPort } from "./types";

/** A call to the Files API is answered at once or not at all. */
const CONNECT_TIMEOUT_MS = 15_000;

export type LiveFilesConfig = {
  userId: string;
  workspaceId: string;
  options: RegionalOptions;
  api?: FilesApiClient;
  put?: Put;
};

/** A short-lived link to read a file from, and when it stops working. */
export type FileLink = { url: string; expiresAt: Date };

/** The Files API port, which can also sign many files' links at once. */
export type LiveFilesPort = FilesPort & {
  /**
   * Links for many files, at most `URLS_PER_REQUEST_MAX` to a request. A file that cannot be read,
   * whichever the reason, has none in the answer.
   */
  openAll(ids: readonly string[], signal?: AbortSignal): Promise<Map<string, FileLink>>;
};

/**
 * File storage through the Files API: `start` records the file and signs an upload, the bytes go
 * straight to the bucket, and `complete` has the API check what arrived. The same transport as
 * Chats and Connections (the workspace's regional API and a token held in memory), so a change of
 * person or workspace stops an upload in flight.
 */
export function createLiveFilesPort(config: LiveFilesConfig): LiveFilesPort {
  const { userId, workspaceId, options } = config;
  const api = config.api ?? filesApiClient;
  const put = config.put ?? putWithProgress;
  const scope = { userId, workspaceId };

  const request = <T>(signal: AbortSignal | undefined, call: ContractCall<FilesApiClient, T>) =>
    contractRequest(
      api,
      scope,
      {
        ...options,
        timeoutMs: options.timeoutMs ?? CONNECT_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      },
      call,
    );

  const remove = (id: string) =>
    request(undefined, (c, ws, call) =>
      c.remove({ params: { workspaceId: ws, fileId: id } }, call),
    ).then(() => undefined);

  // Asking the token cache for a scope that is not the held one displaces the held one, so
  // follow-up calls of an upload begun in a workspace that has since been left are not made.
  const stillHere = (held: { signal: AbortSignal }) =>
    !held.signal.aborted && options.cache.available(scope);
  const left = () => new AppError("ACCESS_CHANGED");

  return {
    async upload(file, { signal, onProgress }) {
      // The same rule the API applies, read from the name when the browser does not know the type.
      const mediaType = mediaTypeOf(file);
      if (mediaType === null) throw new AppError("FILE_TYPE_NOT_ALLOWED");
      // The signal that ends when the person or workspace changes rides along with the bytes.
      const { started, access } = await request(signal, async (c, ws, call, held) => ({
        started: await c.start(
          { params: { workspaceId: ws }, body: { name: file.name, mediaType, size: file.size } },
          call,
        ),
        access: held,
      }));
      // The row waits as pending until it ages out of the upload cap; remove it now if we can.
      const abandon = () => {
        if (stillHere(access)) void remove(started.file.id).catch(() => undefined);
      };
      try {
        await put(started.upload, file, {
          signal: AbortSignal.any([signal, access.signal]),
          onProgress,
        });
      } catch (error) {
        if (access.signal.aborted) throw left();
        abandon();
        throw error;
      }
      if (!stillHere(access)) throw left();
      let done;
      try {
        done = await request(signal, (c, ws, call) =>
          c.complete({ params: { workspaceId: ws, fileId: started.file.id } }, call),
        );
      } catch (error) {
        abandon();
        throw error;
      }
      return { id: done.id, name: done.name, mediaType: done.mediaType, size: done.size };
    },

    discard: remove,

    async open(id, signal, { download = false } = {}) {
      const answered = await request(signal, (c, ws, call) =>
        c.urls(
          { params: { workspaceId: ws }, body: { ids: [id], ...(download ? { download } : {}) } },
          call,
        ),
      );
      const item = answered.files.find((entry) => entry.id === id);
      if (!item || "refusal" in item) throw new AppError("NOT_FOUND");
      return { url: item.url, expiresAt: new Date(item.expiresAt) };
    },

    async openAll(ids, signal) {
      const unique = [...new Set(ids)];
      const batches: string[][] = [];
      for (let i = 0; i < unique.length; i += URLS_PER_REQUEST_MAX) {
        batches.push(unique.slice(i, i + URLS_PER_REQUEST_MAX));
      }
      const answers = await Promise.all(
        batches.map((batch) =>
          request(signal, (c, ws, call) =>
            c.urls({ params: { workspaceId: ws }, body: { ids: batch } }, call),
          ),
        ),
      );
      const links = new Map<string, FileLink>();
      for (const item of answers.flatMap((answered) => answered.files)) {
        if (!("refusal" in item)) {
          links.set(item.id, { url: item.url, expiresAt: new Date(item.expiresAt) });
        }
      }
      return links;
    },
  };
}
